import { supabase } from '../clients/supabaseClient';
import type {
    Order, OrderFormData, OrderItem, OrderStatus, Payment, WalletTransaction,
    OrderPageRow, OrdersNeedingDriverRow, OrderItemRow,
    CakeOrderWithOrder, CakeLinkedOrder, CakeOrderCustomer, CakeSize, CakeSizePayload, CakeOptionPayload, CakeOptionPrice, CakeOptionPriceUpsert, CakeOption
} from '../types';
import { OPEN_ORDER_STATUSES, ORDER_STATUS } from '../constants/orderStatuses';
import { unwrapSupabaseJoin } from '../utils/supabase';

// ── Order select ───────────────────────────────────────────────────────────────

const ORDER_SELECT = `
    id, reference, status, status_id, type, created_at,
    customer_id, delivery_address_id, store_id,
    parcel_count, total, delivery_fee, delivery_type,
    notes, collection_point_id, shared_basket_id,
    customer:customers (
        id, first_name, last_name, cellphone, delivery_address
    ),
    store:stores ( id, name )
`;

function normaliseRow(row: OrderPageRow): Order {
    const customer = unwrapSupabaseJoin(row.customer);
    return {
        ...row,
        customer,
        deliveries: null,
        notes: row.notes ?? '',
        status_sort_order: row.status_sort_order ?? 0,
        delivery_slot_start: row.delivery_slot_start ?? '',
        delivery_slot_end: row.delivery_slot_end ?? '',
        delivery_slot_display_sast: row.delivery_slot_display_sast ?? '',
        status_obj: {
            id: row.status_id ?? '',
            name: row.status ?? '',
            sort_order: row.status_sort_order ?? 0,
        },
    };
}

// ── Types ──────────────────────────────────────────────────────────────────────

export type FetchOrdersPageParams = {
    page: number;
    pageSize: number;
    activeTab: 'open' | 'delivered';
    statuses?: string[];
    type?: string;
    deliveryType?: string;
    search?: string;
};

export type HomeOrderCounts = {
    open: number;
    awaitingPacking: number;
    packerAssigned: number;
};

export type CollectionOrder = {
    id: string;
    reference: string;
    status: string;
    pin_code: string | null;
    created_at: string;
    delivery_type: string;
    collection_point_id: string | null;
    customers: { first_name: string; last_name: string } | null;
    collection_point: { name: string; address_line1: string } | null;
};

// ── Orders page ────────────────────────────────────────────────────────────────

export async function fetchOrdersPageFromDb(
    params: FetchOrdersPageParams
): Promise<{ rows: Order[]; count: number }> {
    const {
        page, pageSize, activeTab,
        statuses = [], type = 'All',
        deliveryType = 'All', search = '',
    } = params;

    const from = (page - 1) * pageSize;
    const to   = from + pageSize - 1;

    let query = supabase
        .from('orders')
        .select(ORDER_SELECT, { count: 'exact' })
        .in('type', ['chat', 'ecom'])
        .order('created_at', { ascending: false })
        .range(from, to);

    if (activeTab === 'delivered') {
        query = query.in('status', [ORDER_STATUS.DELIVERY_COMPLETE, ORDER_STATUS.COLLECTION_COMPLETE]);
    } else if (statuses.length > 0) {
        query = query.in('status', statuses);
    } else {
        query = query.in('status', OPEN_ORDER_STATUSES);
    }

    if (type !== 'All') query = query.eq('type', type.toLowerCase());

    if (deliveryType && deliveryType !== 'All') {
        if (deliveryType === 'collection') query = query.eq('delivery_type', 'collection');
        else if (deliveryType === 'delivery') query = query.neq('delivery_type', 'collection');
    }

    if (search.trim()) query = query.ilike('reference', `%${search.trim()}%`);

    const { data, error, count } = await query;
    if (error) throw error;

    return {
        rows:  (data ?? []).map(normaliseRow),
        count: count ?? 0,
    };
}

// ── Create order ───────────────────────────────────────────────────────────────

export async function createOrderInDb(order: OrderFormData): Promise<Order> {
    const { data: statusData } = await supabase
        .from('statuses')
        .select('id')
        .eq('name', ORDER_STATUS.CREATED)
        .single();

    const payload = {
        ...order,
        status_id: statusData?.id,
        status:    ORDER_STATUS.CREATED,
        pin_code:  Math.floor(1000 + Math.random() * 9000).toString(),
    };

    const { data, error } = await supabase
        .from('orders')
        .insert([payload])
        .select()
        .single();

    if (error) throw error;

    if (statusData?.id) {
        await supabase.from('order_status_history').insert([{
            order_id:  data.id,
            status_id: statusData.id,
        }]);
    }

    return data as Order;
}

// ── Update order status ────────────────────────────────────────────────────────

export async function updateOrderStatusInDb(
    orderId: string,
    statusName: string,
    metadata?: { model_id: string; model_type: string }
): Promise<boolean> {
    const { data, error } = await supabase.functions.invoke('update-order-status', {
        body: {
            record: {
                id:         orderId,
                status:     statusName,
                model_id:   metadata?.model_id,
                model_type: metadata?.model_type,
            },
        },
    });

    if (error) throw error;
    return data?.success ?? false;
}

// ── Statuses ───────────────────────────────────────────────────────────────────

export async function fetchAllStatusesFromDb(): Promise<{ id: string; name: string }[]> {
    const { data, error } = await supabase.from('statuses').select('id, name');
    if (error) throw error;
    return (data ?? []) as { id: string; name: string }[];
}

// ── Home / overview ────────────────────────────────────────────────────────────

export async function fetchHomeOrderCountsFromDb(): Promise<HomeOrderCounts> {
    const { data, error } = await supabase
        .from('orders')
        .select('id, status')
        .in('status', OPEN_ORDER_STATUSES)
        .in('type', ['ecom', 'chat']);

    if (error) throw error;

    const rows = data ?? [];
    return {
        open:            rows.length,
        awaitingPacking: rows.filter(r => r.status === ORDER_STATUS.AWAITING_PACKING).length,
        packerAssigned:  rows.filter(r => r.status === ORDER_STATUS.PACKER_ASSIGNED).length,
    };
}

export async function fetchOrdersNeedingDriverFromDb(): Promise<Order[]> {
    const { data, error } = await supabase
        .from('orders')
        .select(`
            id, reference, status, status_id, type, created_at,
            customer_id, delivery_address_id, store_id,
            customer:customers ( id, first_name, last_name, delivery_address )
        `)
        .in('type', ['chat', 'ecom'])
        .eq('status', ORDER_STATUS.ASSIGN_DRIVER)
        .order('created_at', { ascending: false })
        .limit(25);

    if (error) throw error;

    return (data ?? []).map((row: OrdersNeedingDriverRow): Order => {
        const customer = unwrapSupabaseJoin(row.customer);
        return {
            ...row,
            customer,
            deliveries:                null,
            parcel_count:              0,
            delivery_fee:              0,
            delivery_type:             '',
            notes:                     '',
            total:                     0,
            collection_point_id:       null,
            delivery_slot_start:       '',
            delivery_slot_end:         '',
            delivery_slot_display_sast: '',
            status_sort_order:         0,
            status_obj: {
                id:         row.status_id ?? '',
                name:       row.status ?? '',
                sort_order: 0,
            },
        };
    });
}

// ── Order detail ───────────────────────────────────────────────────────────────

export async function fetchSingleOrderFromDb(orderId: string): Promise<Order | null> {
    const { data, error } = await supabase
        .from('orders')
        .select(`
            *,
            status_obj:statuses!status_id (id, name, sort_order),
            customer:customers(
                *,
                primary_address:addresses!customers_delivery_address_fkey (
                    id, address_1, suburb, city, postcode
                )
            )
        `)
        .eq('id', orderId)
        .single();

    if (error) throw error;

    const order = data as Order;
    if (order?.customer) {
        order.customer.primary_address = unwrapSupabaseJoin(order.customer.primary_address);
    }
    return order;
}

export async function fetchOrderItemsFromDb(orderId: string): Promise<OrderItem[]> {
    const { data, error } = await supabase
        .from('order_items')
        .select(`
            id, order_id, product_id, quantity, created_at, product_prices_id,
            price_record:product_prices (price),
            product:products (name, sku)
        `)
        .eq('order_id', orderId)
        .is('deleted_at', null);

    if (error) throw error;

    return (data ?? []).map((item: OrderItemRow) => {
        const priceRecord = unwrapSupabaseJoin(item.price_record);
        const product     = unwrapSupabaseJoin(item.product);
        const unitPrice   = Number(priceRecord?.price ?? 0);
        return {
            id:           item.id,
            order_id:     item.order_id,
            product_id:   item.product_id,
            product_name: product?.name ?? 'Unknown Product',
            sku:          product?.sku  ?? 'N/A',
            quantity:     item.quantity,
            unit_price:   unitPrice,
            total_price:  unitPrice * item.quantity,
            created_at:   item.created_at,
        };
    });
}

export async function fetchOrderPaymentsFromDb(orderId: string): Promise<Payment[]> {
    const { data, error } = await supabase
        .from('payments')
        .select('*, status:statuses(name)')
        .eq('order_id', orderId);

    if (error) throw error;
    return (data ?? []) as Payment[];
}

export async function fetchWalletTransactionsFromDb(orderId: string): Promise<WalletTransaction[]> {
    const { data, error } = await supabase
        .from('wallet_transactions')
        .select('*')
        .eq('source_order_id', orderId)
        .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []) as WalletTransaction[];
}

export async function deleteOrderItemFromDb(
    orderId: string,
    itemId: string,
    reason: string
): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    const adminId = session?.user?.id ?? 'system';
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;

    const response = await fetch(`${supabaseUrl}/functions/v1/admin-change-order`, {
        method:  'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization:  `Bearer ${session?.access_token ?? ''}`,
        },
        body: JSON.stringify({
            order_id:   orderId,
            item_id:    itemId,
            reason,
            action:     'delete',
            changed_by: adminId,
        }),
    });

    if (!response.ok) {
        const text = await response.text();
        let message = `HTTP ${response.status}: ${text}`;
        try {
            const json = JSON.parse(text);
            if (json.error) message = json.error;
        } catch { /* not JSON */ }
        throw new Error(message);
    }
}

export async function submitOrderItemEditToDb(
    orderId: string,
    itemId: string,
    previousQuantity: number,
    newQuantity: number,
    reason: string
): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    const adminId = session?.user?.id ?? 'system';

    const { error } = await supabase.functions.invoke('admin-change-order', {
        body: {
            order_id:   orderId,
            item_id:    itemId,
            previous:   previousQuantity,
            current:    newQuantity,
            reason,
            action:     'quantity_change',
            changed_by: adminId,
        },
    });

    if (error) throw new Error(error.message ?? 'Failed to update item');
}

export async function fetchOrderStatusHistoryFromDb(orderId: string): Promise<OrderStatus[]> {
    const { data, error } = await supabase
        .from('order_status_history')
        .select(`
            id, status_id, created_at, model_id, model_type,
            statuses (id, name, sort_order, type)
        `)
        .eq('order_id', orderId)
        .order('created_at', { ascending: true });

    if (error) throw error;

    return (data ?? []).map((item) => {
        const statuses = item.statuses;
        const status   = Array.isArray(statuses) ? statuses[0] ?? null : statuses ?? null;
        return {
            id:         item.id,
            status_id:  item.status_id,
            created_at: item.created_at,
            model_id:   item.model_id,
            model_type: item.model_type,
            status,
        };
    }) as OrderStatus[];
}

export async function fetchOrdersWithoutDeliveriesFromDb(): Promise<Order[]> {
    const { data, error } = await supabase
        .from('orders_without_deliveries')
        .select(`*, customer:customers(*)`)
        .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []) as unknown as Order[];
}

// ── Collections ────────────────────────────────────────────────────────────────

export async function fetchCollectionOrdersFromDb(): Promise<CollectionOrder[]> {
    const { data, error } = await supabase
        .from('orders')
        .select(`
            id, reference, status, pin_code, created_at, delivery_type, collection_point_id,
            customers:customer_id ( first_name, last_name ),
            collection_point:collection_point_id ( name, address_line1 )
        `)
        .eq('delivery_type', 'collection')
        .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []) as unknown as CollectionOrder[];
}

// ── Cake orders ────────────────────────────────────────────────────────────────

export async function fetchCakeOrdersFromDb(): Promise<CakeOrderWithOrder[]> {
    const { data: cakeOrders, error } = await supabase
        .from('cake_orders')
        .select('*')
        .order('created_at', { ascending: false });

    if (error) throw error;
    if (!cakeOrders || cakeOrders.length === 0) return [];

    const orderIds = [...new Set(cakeOrders.map(c => c.order_id))];

    const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select('id, reference, status, customer:customers(first_name, last_name, cellphone)')
        .in('id', orderIds);

    if (orderError) throw orderError;

    const orderMap: Record<string, CakeLinkedOrder> = {};
    (orderData ?? []).forEach((o: {
        id: string;
        reference: string;
        status: string;
        customer: CakeOrderCustomer | CakeOrderCustomer[] | null;
    }) => {
        orderMap[o.id] = {
            id:       o.id,
            reference: o.reference,
            status:   o.status,
            customer: unwrapSupabaseJoin(o.customer),
        };
    });

    return cakeOrders.map(c => ({ ...c, order: orderMap[c.order_id] ?? null }));
}

export async function updateCakeOrderStatusInDb(
    orderId: string,
    statusName: string
): Promise<void> {
    const { data: statusRow } = await supabase
        .from('statuses')
        .select('id')
        .eq('name', statusName)
        .single();

    const updatePayload: { status: string; status_id?: string } = { status: statusName };
    if (statusRow) updatePayload.status_id = statusRow.id;

    const { error } = await supabase
        .from('orders')
        .update(updatePayload)
        .eq('id', orderId);

    if (error) throw error;

    if (statusRow) {
        await supabase.from('order_status_history').insert({
            order_id:  orderId,
            status_id: statusRow.id,
        });
    }
}

// ── Cake sizes ─────────────────────────────────────────────────────────────────


export async function fetchCakeSizesFromDb(): Promise<CakeSize[]> {
    const { data, error } = await supabase
        .from('cake_sizes')
        .select('*')
        .order('sort_order', { ascending: true });
    if (error) throw error;
    return (data ?? []) as CakeSize[];
}

export async function createCakeSizeInDb(payload: CakeSizePayload): Promise<void> {
    const { error } = await supabase
        .from('cake_sizes')
        .insert({ ...payload, active: true });
    if (error) throw error;
}

export async function updateCakeSizeInDb(id: string, payload: CakeSizePayload): Promise<void> {
    const { error } = await supabase
        .from('cake_sizes')
        .update(payload)
        .eq('id', id);
    if (error) throw error;
}

export async function toggleCakeSizeActiveInDb(id: string, active: boolean): Promise<void> {
    const { error } = await supabase
        .from('cake_sizes')
        .update({ active })
        .eq('id', id);
    if (error) throw error;
}

export async function deleteCakeSizeFromDb(id: string): Promise<void> {
    const { error } = await supabase
        .from('cake_sizes')
        .delete()
        .eq('id', id);
    if (error) throw error;
}


// ── Cake options ───────────────────────────────────────────────────────────────

export async function fetchCakeOptionsFromDb(): Promise<CakeOption[]> {
    const { data, error } = await supabase
        .from('cake_options')
        .select('*')
        .order('category')
        .order('sort_order');
    if (error) throw error;
    return (data ?? []) as CakeOption[];
}

export async function fetchCakeOptionPricesFromDb(): Promise<CakeOptionPrice[]> {
    const { data, error } = await supabase
        .from('cake_option_prices')
        .select('*');
    if (error) throw error;
    return (data ?? []) as CakeOptionPrice[];
}

export async function createCakeOptionInDb(
    payload: CakeOptionPayload,
    prices: CakeOptionPriceUpsert[]
): Promise<void> {
    const { data, error } = await supabase
        .from('cake_options')
        .insert({ ...payload, active: true })
        .select('id')
        .single();
    if (error) throw error;

    if (prices.length > 0) {
        const { error: priceError } = await supabase
            .from('cake_option_prices')
            .insert(prices.map(p => ({ ...p, option_id: data.id })));
        if (priceError) throw priceError;
    }
}

export async function updateCakeOptionInDb(
    id: string,
    payload: CakeOptionPayload,
    priceUpdates: { existingId: string; price: number }[],
    priceInserts: CakeOptionPriceUpsert[]
): Promise<void> {
    const { error } = await supabase
        .from('cake_options')
        .update(payload)
        .eq('id', id);
    if (error) throw error;

    await Promise.all([
        ...priceUpdates.map(({ existingId, price }) =>
            supabase.from('cake_option_prices').update({ price }).eq('id', existingId)
        ),
        ...priceInserts.length > 0
            ? [supabase.from('cake_option_prices').insert(priceInserts)]
            : [],
    ]);
}

export async function toggleCakeOptionActiveInDb(id: string, active: boolean): Promise<void> {
    const { error } = await supabase
        .from('cake_options')
        .update({ active })
        .eq('id', id);
    if (error) throw error;
}

export async function deleteCakeOptionFromDb(id: string): Promise<void> {
    await supabase.from('cake_option_prices').delete().eq('option_id', id);
    const { error } = await supabase.from('cake_options').delete().eq('id', id);
    if (error) throw error;
}